import api from '../axios'
import { normalizeContentApplyStages } from '@/utils/store/contentApplyStage'
import { normalizeContentResource, resolveContentCoverUrl } from '@/utils/contentResource'

function normalizeArrayValue(value) {
  if (Array.isArray(value))
    return value.filter((item) => item !== undefined && item !== null && item !== '')
  if (value === undefined || value === null || value === '') return []
  return [value]
}

function normalizePositiveInt(value, fallback) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : fallback
}

function resolveSearchCategoryId(params = {}) {
  const secondaryId = normalizeArrayValue(params.secondaryCategoryIds)[0]
  return secondaryId || params.categoryId || params.primaryCategoryId || undefined
}

function normalizeFilterParamList(filterParamList) {
  if (!Array.isArray(filterParamList)) return []
  return filterParamList
    .map((item) => {
      const paramItemId = item?.paramItemId
      const subItemList = normalizeArrayValue(item?.subItemList)
        .map((subItem) => {
          const subParamValue = subItem?.subParamValue ?? subItem?.tagValue ?? subItem
          return subParamValue === undefined || subParamValue === null || subParamValue === ''
            ? null
            : { subParamValue: String(subParamValue) }
        })
        .filter(Boolean)

      if (
        paramItemId === undefined ||
        paramItemId === null ||
        paramItemId === '' ||
        subItemList.length === 0
      ) {
        return null
      }

      // B9：子参数独立筛选——paramSubItemId 必须透传，否则同父不同子的
      // 相同标签值会在 search 侧被错误合并回父维度
      const paramSubItemId = item?.paramSubItemId
      const normalized = {
        paramItemId: String(paramItemId),
        subItemList,
      }
      if (paramSubItemId !== undefined && paramSubItemId !== null && paramSubItemId !== '') {
        normalized.paramSubItemId = String(paramSubItemId)
      }
      return normalized
    })
    .filter(Boolean)
}

function normalizeProductSearchPayload(params = {}) {
  const payload = {
    current: normalizePositiveInt(params.current, 1),
    size: normalizePositiveInt(params.size, 10),
  }

  const categoryId = resolveSearchCategoryId(params)
  if (categoryId !== undefined && categoryId !== null && categoryId !== '') {
    payload.categoryId = String(categoryId)
  }

  const filterParamList = normalizeFilterParamList(params.filterParamList)
  if (filterParamList.length > 0) {
    payload.filterParamList = filterParamList
  }

  const prodIds = normalizeArrayValue(params.prodIds).map((item) => String(item))
  if (prodIds.length > 0) {
    payload.prodIds = prodIds
  }

  const labelPick = normalizeArrayValue(params.labelPick).map((item) => String(item))
  if (labelPick.length > 0) {
    payload.labelPick = labelPick
  }

  const statusList = normalizeArrayValue(params.statusList)
    .map(Number)
    .filter((item) => Number.isFinite(item))
  if (statusList.length > 0) {
    payload.statusList = statusList
  }

  const prodName = params.prodName
  if (prodName !== undefined && prodName !== null && String(prodName).trim() !== '') {
    payload.prodName = String(prodName).trim()
  }

  if (params.keyword !== undefined && params.keyword !== null && params.keyword !== '') {
    payload.keyword = String(params.keyword)
  }

  if (params.containsDelisted !== undefined && params.containsDelisted !== null) {
    payload.containsDelisted = Boolean(params.containsDelisted)
  }

  if (params.containsUnlisted !== undefined && params.containsUnlisted !== null) {
    payload.containsUnlisted = Boolean(params.containsUnlisted)
  }

  const isDelisted = Number(params.isDelisted)
  if (Number.isFinite(isDelisted)) {
    payload.isDelisted = Math.trunc(isDelisted)
  }

  const sort = Number(params.sort)
  if (Number.isFinite(sort)) {
    payload.sort = Math.trunc(sort)
  }

  const sortType = Number(params.sortType)
  if (Number.isFinite(sortType)) {
    payload.sortType = Math.trunc(sortType)
  }

  return payload
}

function normalizeStoreContentRecord(item) {
  if (!item || typeof item !== 'object') return item
  const contentId = item.contentId ?? item.id
  const primaryResource = item.primaryResource || {}
  const videoCover = item.videoCover || {}
  const normalizedPrimary = normalizeContentResource(primaryResource, item)
  const fileUrl = item.fileUrl || normalizedPrimary.previewUrl || normalizedPrimary.url || ''
  const applyStages = normalizeContentApplyStages(item.applyStages)
  const coverUrl = resolveContentCoverUrl(videoCover, item)

  return {
    ...item,
    applyStage: item.applyStage ?? applyStages[0],
    applyStages,
    attachmentInfoList: (item.attachmentInfoList || item.attachments || []).map((attachment) =>
      normalizeContentResource(attachment)
    ),
    contentId,
    coverUrl,
    filePath: item.filePath || fileUrl,
    fileUrl,
    primaryResource: normalizedPrimary,
    videoCover: normalizeContentResource(videoCover),
    prodNames: item.prodNames || normalizeArrayValue(item.products).map((product) => product.label),
  }
}

/**
 * @description 商城商品相关 API (PC端接口)
 */
export const productApi = {
  /**
   * @description 获取产品配置预览内容（缓存）
   * @param {string} uuid - 预览 uuid
   * @param {Object} [config] - axios request config（可传 headers 等）
   * @returns {Promise<Array>} 内容块列表
   */
  getContentArrayCache: (uuid, config = {}) =>
    api.get(`/prod/contentArray/cache/${encodeURIComponent(String(uuid || '').trim())}`, {
      showError: false,
      ...config,
    }),

  /**
   * @description 获取商品核心详情 (PC端专用)
   * @param {Object} params - 查询参数 { prodId, addrId, stationId, needPointStock, ... }
   * @returns {Promise<Object>} 商品详情 (ProductVO)。后端可返回 `afterSalePolicy`（如 `"2_7_15"` 表示保修年_退货天_换货天），或 `warrantyYears` / `returnDays` / `exchangeDays`
   */
  getProductDetail: (params, config = {}) =>
    api.get('/p/store/product/info', {
      params,
      allowBizCodes: ['A00005'],
      showError: false,
      ...config,
    }),

  /**
   * @description 获取相关方案列表
   * @param {Object} data - 查询条件
   * @returns {Promise<Object>} 方案列表
   * @deprecated 商品方案关系迁入 TDC 前不得调用；商品详情当前固定展示空关联列表。
   */
  getSolutions: (data) => api.post('/p/solution/list', data, { showError: false }),

  /**
   * @description 分页获取商品评价
   * @param {Object} params - 查询参数 { prodId, current, size, evaluate, ... }
   * @returns {Promise<Object>} 评价分页数据
   */
  getProductReviews: (params) => api.get('/prod/prodCommPageByProd', { params, showError: false }),

  /**
   * @description 获取商品评价统计数据
   * @param {Object} params - 查询参数 { prodId, stationId }
   * @returns {Promise<Object>} 评价统计（总数/好评率/各标签计数）
   */
  getProductReviewStats: (params) => api.get('/prod/prodCommData', { params, showError: false }),

  /**
   * @description 检查是否已收藏该商品（TDC store 端点，返回 { isCollection }）
   * @param {string|number} prodId - 商品 ID
   * @returns {Promise<boolean>} 是否收藏
   */
  checkIsFavorite: (prodId) =>
    api
      .get('/p/store/user/collection/isCollection', { params: { prodId }, showError: false })
      .then((res) => res?.isCollection === true),

  /**
   * @description 切换商品收藏状态（TDC store 端点，返回 { isCollection }）
   * @param {string|number} prodId - 商品 ID
   * @returns {Promise<boolean>} 返回 true 表示已收藏，false 表示已取消收藏
   */
  toggleFavorite: (prodId) =>
    api
      .post('/p/store/user/collection/addOrCancel', { prodId })
      .then((res) => res?.isCollection === true),

  /**
   * @description 分页获取收藏的商品列表（TDC store 端点，返回 { records, total, current, size }）
   * @param {Object} params - 查询参数 { current, size, prodName }
   * @returns {Promise<Object>} 商品分页数据
   */
  /** 我的收藏商品（用户相关，失败需提示） */
  getFavoriteProducts: (params) => api.get('/p/store/user/collection/prods', { params }),

  /**
   * @description 获取用户收藏商品 id 列表（TDC store 端点，返回 { prodIds: string[] }）
   * @param {Object} params - 查询参数 { current, size, prodName }
   * @returns {Promise<string[]>} 收藏商品 id 数组
   */
  /** 我的收藏商品 id（用户相关，失败需提示） */
  getFavoriteProductIds: (params) =>
    api
      .get('/p/store/user/collection/prodIds', { params })
      .then((res) => (Array.isArray(res?.prodIds) ? res.prodIds : [])),

  /**
   * @description 获取收藏夹商品总数（TDC store 端点，返回 { count }）
   * @returns {Promise<number>} 商品总数
   */
  /** 我的收藏数量（用户相关，失败需提示） */
  getCollectionCount: () =>
    api.get('/p/store/user/collection/collectionCount').then((res) => Number(res?.count) || 0),

  /**
   * @description 获取商品可领取优惠券列表（V2）
   * @param {string|number} prodId - 商品 ID
   * @returns {Promise<Array>} 优惠券列表
   */
  getCoupons: (prodId) =>
    api.get('/p/myCoupon/listByProdId/v2', { params: { prodId }, showError: false }),

  /**
   * @description 领取优惠券
   * @param {string|number} couponId - 优惠券 ID
   * @returns {Promise<Object>} 操作结果
   */
  claimCoupon: (couponId) => api.post('/p/myCoupon/receive', couponId, { params: { couponId } }),

  /**
   * @description 商品券列表(商家优惠券) 游客访问
   * @param {Object} params - 分页参数 { current, size }
   * @returns {Promise<Array>} 优惠券列表
   */
  getCouponListGuest: (params) => api.get('/coupon/getCouponPage', { params, showError: false }),

  /**
   * @description 商品券列表(商家优惠券) 已登录
   * @param {Object} params - 分页参数 { current, size }
   * @returns {Promise<Array>} 优惠券列表
   */
  getCouponListLogged: (params) =>
    api.get('/p/myCoupon/getCouponPage', { params, showError: false }),

  /**
   * @description 获取用户优惠券列表（分页）
   * @param {Object} params - 查询参数 { current, size, status }
   * @param {number} params.current - 当前页码
   * @param {number} params.size - 每页大小
   * @param {number} params.status - 优惠券状态：0-失效，1-有效，2-使用过
   * @returns {Promise<Object>} 优惠券分页数据
   */
  /** 我的优惠券列表（用户相关，失败需提示） */
  getUserCoupons: (params) => {
    return api.get('/p/myCoupon/getCouponList', { params })
  },

  /**
   * @description 获取用户优惠券各状态数量
   * @returns {Promise<Object>} 状态数量 Map（key: 状态码, value: 数量）
   */
  /** 我的优惠券各状态数量（用户相关，失败需提示） */
  getMyCouponStatusCount: () => api.get('/p/myCoupon/getMyCouponsStatusCount'),

  /**
   * @description 获取用户收货地址列表
   * @returns {Promise<Object>} 地址分页数据
   */
  /** 我的地址（用户相关，失败需提示） */
  getUserAddresses: () => api.get('/user/addr/page'),

  /**
   * @description 获取配送城市列表/定位城市
   * @param {Object} [params] - 查询参数
   * @param {number} [params.cityAreaId] - 城市区域ID（可选），不传则返回全量城市列表
   * @returns {Promise<Array>} 城市列表，结构为 FacilitateCityPinYinVO[]
   */
  getDeliveryCities: (params) =>
    api.get('/facilitate/product/getCity', { params, showError: false }),

  /**
   * @description 获取商品资料下载列表
   * @param {string|number} prodId - 商品 ID
   * @returns {Promise<Array>} 资料列表
   */
  getProductDownloads: (prodId) =>
    api
      .get('/p/store/content/listByProdId', {
        params: { centerType: 'DOWNLOAD', prodId },
        showError: false,
      })
      .then((data) => normalizeArrayValue(data?.data || data).map(normalizeStoreContentRecord)),

  getContentDetail: (contentId) =>
    api
      .get('/p/store/content/info', {
        params: { contentId },
        showError: false,
      })
      .then((data) => normalizeStoreContentRecord(data?.data || data)),

  /**
   * @description 获取内容分类树；接口返回顺序即后台内容分类列表的展示优先级
   * @returns {Promise<Array>} 内容分类树
   */
  getContentCategoryTree: () =>
    api
      .get('/p/store/content-category/tree', { showError: false })
      .then((data) => normalizeArrayValue(data?.data || data)),

  /**
   * @description 获取全部分类信息列表
   * @returns {Promise<Object>} 分类信息列表
   */
  getCategoryList: () => api.get('/prod/prod/getCategoryProd', { showError: false }),

  /**
   * @description 获取商品筛选参数配置详情
   * @param {Object} params - 查询参数 { categoryId }
   * @param {Object} [config] - axios request config
   * @returns {Promise<Object>} 筛选参数配置
   */
  getFilterParams: (params, config = {}) =>
    api.get('/p/store/product/filter/facet', { params, showError: false, ...config }),

  /**
   * @description 商品搜索分页接口
   * @param {Object} params - 查询参数 { sort, secondaryCategoryIds, current, size, labelPick, ... }
   * @returns {Promise<Object>} 搜索结果分页数据
   */
  /**
   * @description 获取已退市商品分页列表 (C 端接口)
   * @param {Object} params - 查询参数 { page, size }
   * @returns {Promise<Object>} 分页数据 { records, total, current, size }
   */
  getDelistedProducts: (params = {}, config = {}) =>
    api.get('/p/store/product/delisted', { params, showError: false, ...config }),

  searchProducts: (params, config = {}) => {
    return api.post('/p/store/product/filter/search', normalizeProductSearchPayload(params), {
      showError: false,
      ...config,
    })
  },

  /**
   * @description 获取商品旧搜索结果分页 (GET 方式)
   * @param {Object} params - 查询参数 { current, size, prodType, isActive, ... }
   * @returns {Promise<Object>} 搜索结果分页数据
   */
  getOldSearchProducts: (params) => api.get('/search/page', { params, showError: false }),

  /**
   * @description 获取商品 SKU 列表
   * @param {Object} params - 查询参数 { prodId, stationId, deliveryType }
   * @returns {Promise<Array>} SKU 列表
   */
  getSkuList: (params = {}) => {
    const { comboId: _comboId, ...query } = params
    return api.get('/prod/skuList', { params: query, showError: false })
  },

  /**
   * @description 查询有效的商品标签
   * @param {Object} [params] - 查询参数
   * @param {number|string} [params.firstCategoryId] - 一级分类 ID
   * @param {Object} [config] - axios request config
   * @returns {Promise<Array>} 标签列表
   */
  getLabel: (params, config = {}) =>
    api.get('/prod/prod/getLabel', { params, showError: false, ...config }),

  /**
   * @description 提交商品评价
   * @param {Array|Object} data - 评价数据
   * @returns {Promise<Object>} 操作结果
   */
  saveReview: (data) => api.post('/p/prodComm', data),

  /**
   * @description 根据订单号获取评论列表
   * @param {string} orderNumber - 订单号
   * @returns {Promise<Array>} 评价列表
   */
  getReviewByOrderNumber: (orderNumber) =>
    api.get('/p/prodComm/list', { params: { orderNumber }, showError: false }),
}
